import React, { useRef, useEffect, useState } from 'react';
import { Button } from '../button/Button';
import { Loading } from '../loading/Loading';
import { useSpring, animated } from 'react-spring/web.cjs';
import './QuantityInput.scss';

interface QuantityInputProps {
  handleValue: (value: number) => void;
  flat?: boolean;
  limit?: number;
  loading?: boolean;
  onClick?: () => void;
  outOfStock?: boolean;
  small?: boolean;
  tooltip?: string;
  defaultValue: number;
}

export const QuantityInput: React.FC<QuantityInputProps> = ({
  flat,
  handleValue,
  limit,
  loading,
  onClick,
  outOfStock,
  small,
  tooltip = `Quantity Limit: ${limit}`,
  defaultValue
}) => {
  const [quantity, setQuantity] = useState(defaultValue);
  const [input, setInput] = useState(false);
  const [showInput, setShowInput] = useState(false);
  const [showTooltip, setShowTooltip] = useState(false);
  const [activeInput, setActiveInput] = useState(false);
  const [showValueButton, setShowValueButton] = useState(false);

  const node = useRef<HTMLInputElement>(null);
  const quantityInputRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (quantity > 0 && !showInput) {
      setShowValueButton(true);
    }

    if (quantity < 1) {
      setShowValueButton(false);
    }

    if (quantity > 0 && !showValueButton) {
      setShowInput(true);
    }

    if (quantity > 0 && showValueButton) {
      setShowInput(false);
    }

    if (quantity === limit) {
      setShowTooltip(true);
    } else {
      setShowTooltip(false);
    }

    handleValue(quantity);
    document.addEventListener('click', handleInput);
    return () => {
      document.removeEventListener('click', handleInput);
    };
  }, [input, quantity, showInput]);

  const handleKeyPressLimit = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (limit) {
      if (parseInt(e.target.value) > limit) {
        setShowTooltip(true);
        setQuantity(limit);
      } else {
        setQuantity(parseInt(e.target.value));
      }
    } else {
      setQuantity(parseInt(e.target.value));
    }
  };

  const handleInput = (e: any) => {
    if (null !== node.current) {
      if (node.current.contains(e.target)) {
        return setInput(true);
      } else if (input) {
        setActiveInput(false);
        setInput(false);
      } else if (!quantityInputRef.current?.contains(e.target)) {
        if (e.target.getAttribute('class') != 'ads-quantity-input__input') {
          setShowInput(false);
        }
      }
    }
  };

  const handleAddButtonTab = () => {
    if (!showTooltip) {
      setShowInput(true);
      setShowValueButton(false);
      setQuantity(quantity + 1);
      setActiveInput(true);
    } else {
      setShowInput(true);
      setShowValueButton(false);
    }
  };

  const handleDisabledButton = () => {
    if (!showTooltip) {
      handleAddButton();
    }
  };

  const handleNumberButton = () => {
    setShowInput(true);
    setShowValueButton(false);
  };

  const handleAddButton = () => {
    if (onClick) {
      onClick();
    }
    setShowInput(true);
    setQuantity(quantity + 1);
  };

  const openElement = useSpring({
    config: {
      tension: 200,
      friction: 24,
      clamp: true
    },
    width: showInput ? '100%' : '70%',
    opacity: showInput ? 1 : 0
  });

  const animateQty = useSpring({
    config: {
      tension: 200,
      friction: 5,
      clamp: false
    },
    opacity: showInput ? 1 : 0,
    marginTop: showInput ? 0 : -20
  });

  return (
    <div
      className="ads-quantity-input"
      ref={quantityInputRef}
      data-testid="ads-quantity-input"
    >
      {showInput ? (
        loading ? (
          <animated.div
            style={openElement}
            className={`ads-quantity-input__button ads-quantity-input__button--active ${
              flat ? 'ads-quantity-input__flat' : ''
            } `}
          >
            <Button
              onClick={() => quantity !== 0 && setQuantity(quantity - 1)}
              iconOnly="remove"
              small
              type="inverse"
              disabled
              tabIndex={-1}
            />
            <div className="ads-quantity-input__input-container" ref={node}>
              <Loading small />
            </div>

            <span className="ads-quantity-input__button-add">
              {quantity === limit && (
                <span>
                  <p className="ads-quantity-input__button-tooltip">
                    Limit reached
                  </p>
                  <div className="ads-quantity-input__button-tooltip-pointer"></div>
                </span>
              )}
              <Button
                onClick={() => setQuantity(quantity + 1)}
                iconOnly="add"
                small
                type="inverse"
                disabled
                tabIndex={-1}
              />
            </span>
          </animated.div>
        ) : (
          <animated.div
            style={openElement}
            className={`ads-quantity-input__button ads-quantity-input__button-minus ads-quantity-input__button--active ${
              flat ? 'ads-quantity-input__flat' : ''
            } ${small ? 'ads-quantity-input__small' : ''}`}
          >
            <Button
              onClick={() => quantity !== 0 && setQuantity(quantity - 1)}
              data-testid="ads-quantity-input--minus-button"
              iconOnly="remove"
              small
              type="inverse"
            />
            <div
              className="ads-quantity-input__input-container"
              ref={node}
              onClick={() => setActiveInput(true)}
            >
              {activeInput ? (
                <animated.input
                  style={animateQty}
                  className="ads-quantity-input__input-border"
                  type="text"
                  value={quantity}
                  onChange={(e) => handleKeyPressLimit(e)}
                  onKeyPress={(e) => {
                    isNaN(parseInt(e.key)) && e.preventDefault();
                  }}
                />
              ) : (
                <animated.p
                  style={animateQty}
                  className={`ads-quantity-input__input`}
                  onClick={(e) => handleInput(e)}
                >
                  Qty:
                  <span
                    className="ads-quantity-input__input-value"
                    data-testid="ads-quantity-input__input-value"
                  >
                    {quantity}
                  </span>
                </animated.p>
              )}
            </div>

            <span
              className="ads-quantity-input__button-add"
              onKeyDown={(e) => {
                if (e.key === 'Tab' && !e.shiftKey) {
                  setShowInput(false);
                }
              }}
            >
              {showTooltip && (
                <span>
                  <p
                    className="ads-quantity-input__button-tooltip"
                    data-testid="ads-quantity-input__button-tooltip"
                  >
                    {tooltip}
                  </p>
                  <div className="ads-quantity-input__button-tooltip-pointer"></div>
                </span>
              )}
              <span
                className="ads-quantity-input__button-add-container"
                data-testid="ads-quantity-input--add-button"
                tabIndex={0}
                onKeyPress={(e) => e.key === ' ' && handleDisabledButton()}
              >
                <Button
                  onClick={() => setQuantity(quantity + 1)}
                  iconOnly="add"
                  small
                  type="inverse"
                  disabled={showTooltip}
                />
              </span>
            </span>
          </animated.div>
        )
      ) : (
        <span
          className={`ads-quantity-input__button-container ${
            outOfStock ? 'ads-quantity-input__out-of-stock' : ''
          }`}
          tabIndex={0}
          onKeyPress={(e) => e.key === ' ' && handleAddButtonTab()}
        >
          <div
            data-testid="ads-quantity-input__button"
            className={`ads-quantity-input__button ${
              flat ? 'ads-quantity-input__flat' : ''
            } ${outOfStock ? 'ads-quantity-input__out-of-stock' : ''} ${
              small ? 'ads-quantity-input__small' : ''
            }`}
          >
            {outOfStock && (
              <Button small disabled>
                Out of Stock
              </Button>
            )}

            {flat && !showValueButton && (
              <Button small onClick={() => handleAddButton()}>
                Add to Cart
              </Button>
            )}

            {quantity < 1 && !outOfStock && !flat && (
              <span className={`${small ? 'ads-quantity-input__small' : ''}`}>
                <Button
                  iconOnly="add"
                  small
                  onClick={() => handleAddButton()}
                />
              </span>
            )}

            {showValueButton && (
              <div className="ads-quantity-input__button ads-quantity-input__button-number">
                <div
                  className="ads-quantity-input__button-number--border"
                  onClick={() => handleNumberButton()}
                >
                  <Button type="inverse" number small>
                    <span>{quantity}</span>
                  </Button>
                </div>
              </div>
            )}
          </div>
        </span>
      )}
    </div>
  );
};
